import React from 'react';
import PropTypes from 'prop-types';
import PreviewImage from '../PreviewImage';

/**
 * A picture package, faked in the browser: an 8x10 with a couple of 5x7s and a block of
 * wallets fanned behind it, so the header shows a parent what they are buying rather than
 * one floating cutout.
 *
 * No image processing anywhere — Rails does none, and this needs none. A "sheet" is a
 * PreviewImage (the same component the cards use, which layers the chosen background behind
 * a transparent PNG cutout) inside a white-bordered, rotated, shadowed box. The composite is
 * the browser's; we only place frames.
 *
 * DELIBERATELY UNLABELLED. No "8x10" text on any sheet: the arrangement is generic and is not
 * read from this school's pricelist, so labelling a sheet would promise a product the package
 * may not contain. The shapes read as a picture package on their own.
 *
 * Cost is a handful of requests, not one per frame. Seven frames draw from at most three poses
 * and two backdrops, and the browser caches by URL — so repeats are free however many frames
 * reuse them. The wallet block alone is four frames off a single pair.
 */

// The mockup is built at ONE fixed pixel size and scaled, rather than re-laid-out per
// breakpoint. Two reasons. It keeps a single set of coordinates (a fan of overlapping
// rotated boxes is fiddly enough once), and it dodges the Bootstrap collision: hiding a
// sheet on small screens would want `hidden sm:block`, but Bootstrap defines
// `.hidden { display: none !important }`, which a Tailwind responsive `block` can never
// win against. Scaling shrinks every sheet together and hides nothing.
const DESIGN_W = 260;
const DESIGN_H = 176;

// Print ratios, as width/height: 8x10 and a 2.5x3.5 wallet are both taller than they are
// wide, and a 5x7 is narrower again than an 8x10 — which is why a 5x7 crops the sides of a
// pose that was framed for an 8x10.
const EIGHT_BY_TEN = 8 / 10;
const FIVE_BY_SEVEN = 5 / 7;

// Shadows BY DEPTH, not one shared value. A single shadow on every sheet is the main reason a
// stack reads as stickers pasted flat: real objects at different heights cast differently, so
// the front print throws a large soft shadow and the wallet sheet buried at the back throws
// almost nothing. Two layers each — a tight contact shadow plus a wide diffuse one — because
// that is what a soft light source actually produces.
//
// All offsets are +y (straight down): one light source, consistently above. Mixed directions
// are the other thing that gives a fake stack away.
//
// WARM-TINTED and much lighter than the dark-ground version these replaced. Two reasons a
// straight black shadow fails on ivory: it reads as grey soot rather than shade, and it needed
// 0.45 opacity to register on navy at all — the same value on a near-white ground is a bruise.
// A brown-grey at ~0.18 is what a print actually casts on warm paper.
const SHADOW_HUE = '58,48,38';
const SHEET_SHADOWS = {
  front: `0 10px 20px rgba(${SHADOW_HUE},0.20), 0 2px 5px rgba(${SHADOW_HUE},0.14)`,
  mid: `0 7px 14px rgba(${SHADOW_HUE},0.17), 0 2px 4px rgba(${SHADOW_HUE},0.12)`,
  back: `0 5px 10px rgba(${SHADOW_HUE},0.14), 0 1px 3px rgba(${SHADOW_HUE},0.10)`,
  deep: `0 3px 6px rgba(${SHADOW_HUE},0.12)`,
};

// Fan angles, as one progression rather than four hand-picked numbers. Even 3-degree steps in
// ONE direction, front to back, so the stack reads as arranged; the previous -1/-3/+4/+2 read
// as jitter, which is what "cheap" looks like here.
const FAN_ANGLES = {
  front: -2, mid: 1, back: 4, deep: 7,
};

// A hairline edge, prepended to every sheet's shadow. It does MORE work on the ivory ground
// than it did on the dark one: there, a white sheet was already separated from the ground by
// sheer contrast, and the edge was only needed where two white margins overlapped. Here the
// sheets are near-white on near-white, so the edge is what gives every print its outline.
// Warm-tinted to match the shadows, and a little stronger than the dark-ground version.
const EDGE = `0 0 0 1px rgba(${SHADOW_HUE},0.14)`;

// NO SHEEN. There was one — a white diagonal highlight over each sheet — and it earned its
// place against the deep navy this header used to have, where it read as light catching a
// lustre print. On a warm near-white ground a white highlight over a white-bordered print is
// invisible, and any strength that WOULD show turns the sheet into a foggy panel. Product
// photography on light grounds doesn't use it either; the shadows carry the material.

// One sheet: a white print border around the photo, a depth-appropriate drop shadow, a sheen,
// and a couple of degrees of rotation so the stack reads as physical objects rather than a
// grid of thumbnails.
function Sheet({
  photo, height, ratio, left, top, rotate, z, shadow,
}) {
  if (!photo?.foregroundUrl) return null;

  return (
    <div
      className="absolute rounded-[2px] bg-white"
      style={{
        left: `${left}px`,
        top: `${top}px`,
        height: `${height}px`,
        width: `${height * ratio}px`,
        transform: `rotate(${rotate}deg)`,
        zIndex: z,
        // The white border of a photographic print. Scales with the sheet, so a wallet gets a
        // hairline and the 8x10 gets a visible margin.
        padding: `${Math.max(1, Math.round(height * 0.022))}px`,
        // Hairline edge + this sheet's own depth shadow, in ONE declaration. Tailwind's `ring`
        // and `shadow` utilities both compile to box-shadow and only compose through its own
        // CSS variables — an arbitrary `shadow-[...]` would silently drop a `ring-1`.
        boxShadow: `${EDGE}, ${shadow}`,
      }}
    >
      <div className="relative h-full w-full overflow-hidden">
        <PreviewImage
          foregroundUrl={photo.foregroundUrl}
          backgroundUrl={photo.backgroundUrl}
          // object-cover crops rather than squashes, and 20% down favours the face — the
          // same figure the single-photo header used, for the same reason.
          containerClass="absolute inset-0 z-[1] h-full w-full overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:object-cover [&_img]:object-[center_20%]"
        />
      </div>
    </div>
  );
}

Sheet.defaultProps = { rotate: 0, z: 0 };

Sheet.propTypes = {
  photo: PropTypes.shape({
    foregroundUrl: PropTypes.string,
    backgroundUrl: PropTypes.string,
  }).isRequired,
  height: PropTypes.number.isRequired,
  ratio: PropTypes.number.isRequired,
  left: PropTypes.number.isRequired,
  top: PropTypes.number.isRequired,
  rotate: PropTypes.number,
  z: PropTypes.number,
  // The drop shadow for this sheet's DEPTH — see SHEET_SHADOWS. Required rather than
  // defaulted: one shared shadow across every sheet is what made the fan read as stickers.
  shadow: PropTypes.string.isRequired,
};

export default function PackageMockup({ sheets, className }) {
  // FOUR sheets, in the order a package lists them: the 8x10, the two 5x7s, then the wallet
  // sheet. The wallet block is ONE sheet — four wallets printed on a single piece of paper —
  // which is why it takes one entry and not four.
  const [eightByTen, fiveBySevenA, fiveBySevenB, walletSheet] = sheets;

  if (!eightByTen?.foregroundUrl) return null;

  // Wallets come as a sheet of identical prints, so this is a 2x2 block of one pose — same
  // URL four times, one fetch. Coordinates are hand-placed inside the design box; the
  // right-most sheet ends at 251 of 260, leaving the fan a hair of air on both edges.
  const wallets = [0, 1].flatMap((col) => [0, 1].map((row) => ({ col, row })));

  return (
    // transform-origin on the RIGHT edge: the strip is pinned to the banner's right side, so
    // scaling must shrink toward that edge and not drift away from it.
    <div
      className={className}
      style={{
        width: `${DESIGN_W}px`,
        height: `${DESIGN_H}px`,
        transformOrigin: 'right center',
      }}
    >
      <div className="relative h-full w-full">
        {/* One soft pool under the whole stack, so the package sits on a surface instead of
            floating on the navy. Per-sheet shadows give each print its own lift; this is what
            gives the GROUP a place to stand, and it is the cheapest cue that the fan is a
            physical object rather than artwork.
            Wide and shallow, low opacity, blurred: on a ground this dark a heavier pool reads
            as a smudge. pointer-events-none for the same reason as the sheen. */}
        <div
          className="pointer-events-none absolute"
          style={{
            left: '8px',
            top: '148px',
            width: '232px',
            height: '22px',
            zIndex: 0,
            // Warm-tinted and light, matching the sheets: 0.50 black was tuned for the deep
            // navy this header used to have, and on ivory it would sit under the package like
            // a stain rather than a shadow.
            background: `radial-gradient(ellipse at center, rgba(${SHADOW_HUE},0.18) 0%, rgba(${SHADOW_HUE},0) 72%)`,
            filter: 'blur(3px)',
          }}
        />

        {/* The wallet sheet — smallest, furthest back, tucked to the right. Four frames, ONE
            sheet: they all draw the same entry, so they share its pose and its backdrop, which
            is what a real wallet sheet looks like coming off the press. */}
        {wallets.map(({ col, row }) => (
          <Sheet
            key={`wallet-${col}-${row}`}
            photo={walletSheet}
            height={34}
            ratio={FIVE_BY_SEVEN}
            left={202 + col * 27}
            top={52 + row * 37}
            rotate={FAN_ANGLES.deep}
            z={10}
            shadow={SHEET_SHADOWS.deep}
          />
        ))}

        {/* The 5x7 pair. The far one is sheet three, the near one sheet two, so the fan reads
            front-to-back in package order. */}
        <Sheet
          photo={fiveBySevenB}
          height={100}
          ratio={FIVE_BY_SEVEN}
          left={142}
          top={28}
          rotate={FAN_ANGLES.back}
          z={20}
          shadow={SHEET_SHADOWS.back}
        />
        <Sheet
          photo={fiveBySevenA}
          height={104}
          ratio={FIVE_BY_SEVEN}
          left={94}
          top={34}
          rotate={FAN_ANGLES.mid}
          z={30}
          shadow={SHEET_SHADOWS.mid}
        />

        {/* The 8x10 — largest, front, unobstructed, so the pose a parent picked is the one
            they actually see. Carries the deepest shadow: it is the print nearest the eye. */}
        <Sheet
          photo={eightByTen}
          height={132}
          ratio={EIGHT_BY_TEN}
          left={2}
          top={22}
          rotate={FAN_ANGLES.front}
          z={40}
          shadow={SHEET_SHADOWS.front}
        />
      </div>
    </div>
  );
}

PackageMockup.defaultProps = { className: '' };

PackageMockup.propTypes = {
  // FOUR pose/background pairings, resolved by the caller, in package order: the 8x10, the
  // two 5x7s, then the wallet sheet (one entry — four wallets on one piece of paper). The
  // caller repeats a pose when the customer has favorited fewer, and alternates the backdrop
  // sheet by sheet, so no two adjacent sheets share one.
  sheets: PropTypes.arrayOf(PropTypes.shape({
    foregroundUrl: PropTypes.string,
    backgroundUrl: PropTypes.string,
  })).isRequired,
  className: PropTypes.string,
};
